import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";

import { get } from "@/api";
import { components } from "@/api/generated";
import { Image, Text } from "@/atoms";
import { Button } from "@/atoms/button/Button";
import { IconButton } from "@/atoms/icon-button/IconButton";
import { Modal } from "@/atoms/modal/Modal";
import {
  PhoneNumberField,
  SegmentedToggleField,
  SimpleFormProvider,
  TextField,
} from "@/form";
import { cn } from "@/lib/utils";
import { useHaptics } from "@/modules/haptics/useHaptics/useHaptics";
import Close from "atoms/icon/icons/close.svg";

const recipientOptions = [
  { value: "someone-else", label: "Someone else" },
  { value: "myself", label: "Myself" },
];

const redemptionFormSchema = z
  .object({
    recipientType: z.object(
      {
        value: z.enum(["someone-else", "myself"]),
        label: z.string(),
      },
      { message: "Recipient type is required" }
    ),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phoneNumber: z.string().optional(),
    email: z.string().optional(),
    giftCardItemId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.recipientType.value === "someone-else") {
      if (!data.firstName || data.firstName.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "First name is required",
          path: ["firstName"],
        });
      }
      if (!data.lastName || data.lastName.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Last name is required",
          path: ["lastName"],
        });
      }
      if (!data.phoneNumber || data.phoneNumber.length < 10) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Phone number must be at least 10 digits",
          path: ["phoneNumber"],
        });
      }
      if (data.email && data.email.length > 0) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(data.email)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid email address",
            path: ["email"],
          });
        }
      }
    }
  });

export type RedemptionFormData = z.infer<typeof redemptionFormSchema>;

interface GiftCardRedemptionModalProps {
  open: boolean;
  onClose: () => void;
  giftCards: components["schemas"]["ShopDisplayOrderLineItemModel"][];
  onSubmit: (data: RedemptionFormData) => Promise<void>;
  giftCardPrice: number;
}

const GiftCardRedemptionModal = ({
  open,
  onClose,
  giftCards,
  onSubmit,
  giftCardPrice,
}: GiftCardRedemptionModalProps) => {
  const { data } = useQuery({
    queryKey: ["/gift-cards"],
    queryFn: () => get("/gift-cards", {}),
    enabled: open,
  });

  const [selectedDesignId, setSelectedDesignId] = useState(
    giftCards?.[0]?.id || ""
  );

  const handleFormSubmit = async (formData: RedemptionFormData) => {
    const giftCardItem = data
      ?.find((giftCard) => giftCard.id === selectedDesignId)
      ?.giftCards.find(
        (giftCard) => giftCardPrice === giftCard.baseCostInCents
      );
    if (!giftCardItem) {
      console.error("Gift card item not found", {
        selectedDesignId,
        giftCardPrice,
        availableGiftCards: data,
      });
      throw new Error("Select a Gift Card Design");
    }

    await onSubmit({
      ...formData,
      giftCardItemId: giftCardItem.packageId,
    });
  };

  const handleSuccess = () => {
    onClose();
  };

  const listingsGiftCards = data ?? [];

  return (
    <Modal
      bodyClassName="h-full"
      fullScreen
      open={open}
      modalClassName="h-full"
      onClose={onClose}
    >
      <div className="flex h-full flex-col px-4 pb-6 pt-2">
        <div className="mb-8 grid grid-cols-12 items-center">
          <div className="col-span-1">
            <IconButton
              svg={Close}
              size="small"
              style="iconOnly"
              onClick={onClose}
            />
          </div>
          <div className="col-span-10">
            <h2 className="text-center font-serif text-h2 text-primary">
              Gift Card Details
            </h2>
          </div>
          <div className="col-span-1"></div> {/* Spacer */}
        </div>

        <SimpleFormProvider
          key={open ? "open" : "closed"}
          schema={redemptionFormSchema}
          onSubmit={handleFormSubmit}
          onSuccess={handleSuccess}
          defaultValues={{
            recipientType: { value: "someone-else", label: "Someone else" },
            firstName: "",
            lastName: "",
            phoneNumber: "",
            email: "",
          }}
          resetFields={false}
          hideLoading={false}
        >
          <FormContent
            giftCards={listingsGiftCards}
            selectedDesignId={selectedDesignId}
            setSelectedDesignId={setSelectedDesignId}
          />
        </SimpleFormProvider>
      </div>
    </Modal>
  );
};

interface FormContentProps {
  giftCards: {
    id: string;
    platformId: string;
    specifiedLanguageCode: components["schemas"]["LanguageCode"];
    name: components["schemas"]["Translation"];
    description: components["schemas"]["Translation"];
    giftCards: components["schemas"]["DisplayGiftCard"][];
    locationIds: string[];
    isActive: boolean;
    imageUri: string;
    createdAt: string;
    startDate: string | null;
    endDate: string | null;
  }[];
  selectedDesignId: string;
  setSelectedDesignId: (id: string) => void;
}

const FormContent = ({
  giftCards,
  selectedDesignId,
  setSelectedDesignId,
}: FormContentProps) => {
  const { triggerHaptic } = useHaptics();
  const { watch } = useFormContext<RedemptionFormData>();
  const recipientType = watch("recipientType");

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-col gap-4 rounded-md border border-outline-grey bg-white p-4">
        <Text
          className="text-bold2 text-black"
          text="Who is this for?"
          variant="body1"
        />
        <SegmentedToggleField
          options={recipientOptions}
          name="recipientType"
          label="Recipient Type"
          hideValidation
          hideLabel
        />
      </div>

      <div className="flex flex-col gap-4 rounded-md border border-outline-grey bg-white p-4">
        <Text
          className="text-bold2 text-black"
          text="Pick a design"
          variant="body1"
        />
        <div className="grid grid-cols-2 gap-4">
          {giftCards?.map((giftCard) => (
            <button
              key={giftCard.id}
              type="button"
              onClick={() => {
                triggerHaptic("secondary");
                setSelectedDesignId(giftCard.id);
              }}
              className={cn(
                "relative overflow-hidden rounded-lg shadow-[0px_2px_4px_rgba(0,0,0,0.15)] transition-all hover:ring-2 hover:ring-brand-color/50",
                selectedDesignId === giftCard.id &&
                  "rounded-lg ring-[3px] ring-brand-color"
              )}
            >
              <Image
                alt="Gift Card background"
                className="aspect-16/9 relative inset-0 z-0 object-cover"
                src={giftCard.imageUri ?? ""}
              />
            </button>
          ))}
        </div>
      </div>

      {recipientType?.value === "someone-else" && (
        <div className="flex flex-col gap-4 rounded-md border border-outline-grey bg-white p-4">
          <Text
            className="text-bold2 text-black"
            text="Who's day are you making?"
            variant="body1"
          />

          <div className="flex gap-4">
            <TextField
              name="firstName"
              label=""
              placeholder="First name"
              hideValidation
            />
            <TextField
              name="lastName"
              label=""
              placeholder="Last name"
              hideValidation
            />
          </div>

          <PhoneNumberField
            name="phoneNumber"
            label=""
            placeholder="(123) 456-7890"
            hideValidation
          />

          <TextField
            name="email"
            label=""
            placeholder="Email (optional)"
            hideValidation
          />
        </div>
      )}

      <div className="mt-auto">
        <Button fullWidth submit>
          {recipientType?.value === "myself" ? "Continue" : "Add recipient"}
        </Button>
      </div>
    </div>
  );
};

export default GiftCardRedemptionModal;
